import { __decorate } from "tslib";
import * as focusTrap from 'focus-trap';
import { html, nothing } from 'lit';
import { consume } from '@lit/context';
import debounce from 'debounce';
import { customElement, property, query, state } from 'lit/decorators.js';
import HelixElement from '../../shared/types/helix-element.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { localized, msg } from '@lit/localize';
import { notificationStyles } from './notification.styles.js';
import { classMap } from 'lit/directives/class-map.js';
import '../button/button.js';
import '../icon/icon.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { notificationBarContext, notificationBarCurrentId, } from './notification-bar-context.js';
let HlxNotification = class HlxNotification extends HelixElement {
    constructor() {
        super(...arguments);
        this.notificationId = crypto.randomUUID();
        this.showPrimaryIcon = true;
        this.closable = false;
        this.expandable = false;
        this.primaryLink = '';
        this.isClosed = false;
        this.expanded = false;
        this.secondaryContainerFitsInViewport = true;
        this.slotController = new SlotController(this, 'primary-title', 'primary-description', 'primary-link-text', 'secondary-title', 'secondary-description', 'secondary-button-text');
    }
    async firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.focusTrap = focusTrap.createFocusTrap(this, {
            initialFocus: false,
            tabbableOptions: {
                getShadowRoot: true,
            },
        });
    }
    connectedCallback() {
        super.connectedCallback();
        this.adjustSecondaryContainerDebounced = debounce(() => {
            if (this.expanded) {
                this.adjustSecondaryContainer();
            }
        }, 200);
        window.addEventListener('resize', this.adjustSecondaryContainerDebounced);
        // Forced to subscribe to focus, blur, mouseenter, mouseleave events and fire callbacks on notification-bar level using context
        // because these events is not available on the notification-bar level using standard bubbling
        this.addEventListener('focus', this.focusHandler);
        this.addEventListener('blur', this.blurHandler);
        this.addEventListener('mouseenter', this.mouseEnterHandler);
        this.addEventListener('mouseleave', this.mouseLeaveHandler);
    }
    disconnectedCallback() {
        if (this.expanded) {
            this.handleCollapse();
        }
        clearTimeout(this.secondaryContainerTimerId);
        window.removeEventListener('resize', this.adjustSecondaryContainerDebounced);
        this.adjustSecondaryContainerDebounced.clear();
        this.removeEventListener('focus', this.focusHandler);
        this.removeEventListener('blur', this.blurHandler);
        this.removeEventListener('mouseenter', this.mouseEnterHandler);
        this.removeEventListener('mouseleave', this.mouseLeaveHandler);
        super.disconnectedCallback();
    }
    render() {
        if ((this.currentNotificationId && this.notificationId != this.currentNotificationId) ||
            this.isClosed) {
            return nothing;
        }
        const hasPrimaryLinkText = this.slotController.hasSlot('primary-link-text');
        const hasSecondaryTitle = this.slotController.hasSlot('secondary-title');
        const hasSecondaryButtonText = this.slotController.hasSlot('secondary-button-text');
        return html `
            <div
                role="banner"
                class=${classMap({
            'hlx-notification-has-primary-link-text': hasPrimaryLinkText,
            'hlx-notification-has-secondary-title': hasSecondaryTitle,
            'hlx-notification-has-secondary-button-text': hasSecondaryButtonText,
        })}
                aria-expanded=${this.expanded ? 'true' : 'false'}
                aria-controls="hlx-expandable-content">
                <div
                    class=${classMap({
            'hlx-primary-container': true,
            expandable: this.expandable,
        })}
                    data-testid="primary-container"
                    @click=${this.handleToggle}>
                    ${this.renderPrimaryIcon()}
                    <div class="hlx-text-container">
                        <slot
                            class="hlx-primary-title"
                            data-testid="primary-title"
                            name="primary-title"></slot>
                        <slot class="hlx-primary-description" name="primary-description"></slot>
                        <a
                            tabindex="0"
                            class="hlx-primary-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            @click=${(e) => e.stopPropagation()}
                            href=${this.primaryLink}>
                            <slot name="primary-link-text"></slot>
                        </a>
                    </div>
                    ${this.renderExpandIcon()} ${this.renderCloseIcon()}
                </div>
                <div
                    aria-hidden=${this.expanded ? 'false' : 'true'}
                    id="hlx-expandable-content"
                    class=${classMap({
            'hlx-secondary-container': true,
            'hlx-animation-with-prefers-reduced-motion': true,
            'hlx-expanded': this.expanded,
        })}>
                    <div class="hlx-secondary-container-inner">
                        <div class="hlx-secondary-block">
                            <slot class="hlx-secondary-title" name="secondary-title"></slot>
                            <slot
                                class="hlx-secondary-description"
                                name="secondary-description"></slot>
                            <hlx-button
                                class=${classMap({
            'hlx-secondary-button': true,
            'hlx-hidden': !this.expanded,
        })}
                                kind="primary"
                                variant="dark"
                                size="small"
                                @click=${() => this.emit('hlx-secondary-button-click')}>
                                <slot name="secondary-button-text"></slot>
                            </hlx-button>
                        </div>
                    </div>
                </div>
                <div
                    class=${classMap({
            'hlx-overlay': true,
            'hlx-hidden': !this.expanded,
        })}
                    @click=${this.handleCollapse}></div>
            </div>
        `;
    }
    focusHandler() {
        this.context?.focus();
    }
    blurHandler() {
        this.context?.blur();
    }
    mouseEnterHandler() {
        this.context?.mouseEnter();
    }
    mouseLeaveHandler() {
        this.context?.mouseLeave();
    }
    handleToggle() {
        if (this.expandable) {
            this.expanded ? this.handleCollapse() : this.handleExpand();
        }
    }
    renderExpandIcon() {
        return this.expandable ?
            html `
                    <hlx-button
                        class="hlx-arrow-button"
                        kind="tertiary"
                        size="small"
                        variant="warning"
                        data-testid="arrow-button">
                        <hlx-icon
                            class=${classMap({
                'hlx-arrow-icon': true,
                'hlx-animation-with-prefers-reduced-motion': true,
                'hlx-expanded': this.expanded,
            })}
                            slot="icon"
                            size="14"
                            title=${msg('Arrow icon')}
                            name="chevron-down"></hlx-icon>
                    </hlx-button>
                `
            : nothing;
    }
    renderPrimaryIcon() {
        return this.showPrimaryIcon ?
            html `
                    <hlx-icon
                        class="hlx-primary-icon"
                        size="16"
                        name="triangle-exclamation-filled"
                        title=${msg('Alert icon')}></hlx-icon>
                `
            : nothing;
    }
    renderCloseIcon() {
        return this.closable ?
            html `
                    <hlx-button
                        class="hlx-close-button"
                        kind="tertiary"
                        size="small"
                        variant="warning"
                        data-testid="close-button"
                        @click=${this.handleClose}>
                        <hlx-icon size="14" name="xmark" slot="icon" title=${msg('Close icon')} /></hlx-icon>
                    </hlx-button>
                `
            : nothing;
    }
    handleExpand() {
        this.expanded = true;
        this.emit('hlx-notification-toggle', {
            expanded: true,
        });
        this.context?.toggle(true);
        if (document?.body) {
            document.body.style.overflow = 'hidden';
        }
        this.focusTrap.activate();
        this.secondaryContainerTimerId = setTimeout(() => {
            this.adjustSecondaryContainer();
        }, 350);
    }
    handleCollapse() {
        this.expanded = false;
        this.emit('hlx-notification-toggle', { expanded: false });
        this.context?.toggle(false);
        if (document?.body) {
            document.body.style.overflow = '';
        }
        this.focusTrap?.deactivate();
        this.resetSecondaryContainer();
    }
    handleClose(e) {
        e.stopPropagation();
        if (this.expanded) {
            this.handleCollapse();
        }
        this.emit('hlx-notification-close');
        this.context?.close();
    }
    /**
     * @see https://exas.atlassian.net/browse/HLX-128
     */
    adjustSecondaryContainer() {
        this.secondaryContainerInner.style.overflow = '';
        this.secondaryContainerInner.style.height = '';
        this.secondaryContainerFitsInViewport =
            window.innerHeight > this.secondaryContainerInner.getBoundingClientRect().bottom;
        if (!this.secondaryContainerFitsInViewport) {
            this.secondaryContainerInner.style.overflow = 'auto';
            this.secondaryContainerInner.style.height = `${window.innerHeight - this.secondaryContainerInner.getBoundingClientRect().top}px`;
            this.requestUpdate();
        }
    }
    resetSecondaryContainer() {
        if (!this.secondaryContainerFitsInViewport) {
            this.secondaryContainerInner.style.overflow = '';
            this.secondaryContainerInner.style.height = '';
        }
    }
};
HlxNotification.styles = [componentStyles, notificationStyles];
__decorate([
    consume({ context: notificationBarContext })
], HlxNotification.prototype, "context", void 0);
__decorate([
    consume({ context: notificationBarCurrentId, subscribe: true })
], HlxNotification.prototype, "currentNotificationId", void 0);
__decorate([
    query('.focus-trap')
], HlxNotification.prototype, "focusTrapElement", void 0);
__decorate([
    query('.hlx-secondary-container-inner')
], HlxNotification.prototype, "secondaryContainerInner", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-primary-icon' })
], HlxNotification.prototype, "showPrimaryIcon", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxNotification.prototype, "closable", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxNotification.prototype, "expandable", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'primary-link' })
], HlxNotification.prototype, "primaryLink", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'is-closed' })
], HlxNotification.prototype, "isClosed", void 0);
__decorate([
    state()
], HlxNotification.prototype, "expanded", void 0);
HlxNotification = __decorate([
    customElement('hlx-notification'),
    localized()
], HlxNotification);
export { HlxNotification };
//# sourceMappingURL=notification.js.map